import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { inputsMaxLengths } from '../../../constants';
import styles from './FormContent.module.scss';
import { Button, DateInput, Input } from 'components';
import { useCreatePatient } from 'hooks';
import { appActions } from 'store/app/slice';
import { patientsActions } from 'store/patients/slice';
import { useDispatch } from 'store/utils';
import { PatientCase } from 'types';
import { getDefaultBirthDateString, onlyLatinLettersSymbolsNumbers } from 'utils';

const schema = yup.object({
  id: yup
    .string()
    .test('isNumber', 'Numeric field', (value) => {
      return /^[0-9]*$/.test(value || '');
    })
    .typeError('Numeric field')
    .required('The field is empty'),
  firstname: yup
    .string()
    .matches(onlyLatinLettersSymbolsNumbers, {
      message: 'Only Latin Alphabet is allowed',
    })
    .required('The field is empty'),
  lastname: yup
    .string()
    .matches(onlyLatinLettersSymbolsNumbers, {
      message: 'Only Latin Alphabet is allowed',
    })
    .required('The field is empty'),
  oht: yup
    .string()
    .matches(onlyLatinLettersSymbolsNumbers, {
      message: 'Only Latin Alphabet is allowed',
    })
    .required('The field is empty'),
  date: yup.string().required('The field is empty'),
});

type Props = {
  setCreatedPatientCase(value: PatientCase): void;
  setDisplayError(value: boolean): void;
  onClose(): void;
};

export const FormContent = ({ setCreatedPatientCase, setDisplayError, onClose }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const { paramsData, setUrlParamsData } = useCreatePatient();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      id: paramsData.id || '',
      firstname: paramsData.firstname || '',
      lastname: paramsData.lastname || '',
      oht: paramsData.oht || '',
      date: paramsData.date ? getDefaultBirthDateString(paramsData.date) : '',
    },
  });

  const watchedId = watch('id');
  const watchedFirstname = watch('firstname');
  const watchedLastname = watch('lastname');
  const watchedOht = watch('oht');
  const watchedDate = watch('date');

  useEffect(() => {
    setUrlParamsData({
      id: String(watchedId),
      firstname: watchedFirstname,
      lastname: watchedLastname,
      oht: watchedOht,
      date: watchedDate,
    });
  }, [watchedId, watchedFirstname, watchedLastname, watchedOht, watchedDate, setUrlParamsData]);

  const onSubmit = handleSubmit(async (formData) => {
    if (isLoading) return;
    const { id, firstname, lastname, oht, date } = formData;
    try {
      setIsLoading(true);
      const wrappedResult = dispatch(
        patientsActions.createPatientCase({
          externalPatientId: Number(id),
          firstName: firstname,
          lastName: lastname,
          oralHealthTherapist: oht,
          dateOfBirth: date.split('/').reverse().join('-'),
        }),
      );
      const { isCreated, patientCase } = await wrappedResult.unwrap();
      isCreated && setCreatedPatientCase(patientCase);
      dispatch(appActions.setModalErrorText({ name: 'createPatientError', errorText: '' }));
    } catch (error: any) {
      dispatch(appActions.setModalErrorText({ name: 'createPatientError', errorText: error?.title }));
      setDisplayError(true);
    } finally {
      setIsLoading(false);
    }
  });

  return (
    <div className={styles.formContainer}>
      <form className={styles.form}>
        <Input
          inputName="id"
          labelText="ID"
          register={register}
          registerOptions={{ required: true, maxLength: inputsMaxLengths.id }}
          autoComplete="off"
          placeholder="Enter ID from patient management system"
          maxLength={inputsMaxLengths.id}
          currentValue={watchedId}
          setValue={setValue}
          errorMsg={errors.id?.message}
          characterCounter
        />
        <Input
          inputName="firstname"
          labelText="First name"
          register={register}
          registerOptions={{ required: true, maxLength: inputsMaxLengths.firstname }}
          autoComplete="off"
          placeholder="Enter patient first name"
          maxLength={inputsMaxLengths.firstname}
          currentValue={watchedFirstname}
          setValue={setValue}
          errorMsg={errors.firstname?.message}
          characterCounter
        />
        <Input
          inputName="lastname"
          labelText="Last name"
          register={register}
          registerOptions={{ required: true, maxLength: inputsMaxLengths.lastname }}
          autoComplete="off"
          placeholder="Enter patient last name"
          maxLength={inputsMaxLengths.lastname}
          setValue={setValue}
          currentValue={watchedLastname}
          errorMsg={errors.lastname?.message}
          characterCounter
        />
        <DateInput
          inputName="date"
          labelText="Date of birth"
          register={register}
          setValue={(value: string) => setValue('date', value)}
          errorMsg={errors.date?.message}
          triggerValidation={() => trigger('date')}
        />
        <Input
          inputName="oht"
          labelText="OHT"
          register={register}
          registerOptions={{ required: true, maxLength: inputsMaxLengths.oht }}
          autoComplete="off"
          placeholder="Enter oral health therapist name"
          maxLength={inputsMaxLengths.oht}
          currentValue={watchedOht}
          setValue={setValue}
          errorMsg={errors.oht?.message}
          characterCounter
        />
      </form>
      <div className={styles.buttonRow}>
        <Button isLink onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={onSubmit} isDisabled={!isValid}>
          Create patient
        </Button>
      </div>
    </div>
  );
};
